import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = 'https://ticobaterias.com/api';

type Plantilla = {
  id: number;
  numero: number;
  nombre: string;
  titulo: string;
  activo: boolean;
  fragmentos: number;
  actualizado_en: string | null;
  dias: number | null;
  vencido: boolean;
  tiene_anterior: boolean;
  anterior_en: string | null;
};

type Revision = {
  titulo: string | null;
  fragmentos: { orden: number; titulo: string; caracteres: number; tiene_etiquetas: boolean }[];
  errores: string[];
  advertencias: string[];
  se_puede_publicar: boolean;
};

type Subida = {
  archivoId: number | null;   // null = plantilla nueva
  numero: number | null;
  nombre: string;
  contenido: string;
  revision: Revision;
};

const fecha = (s: string | null) =>
  s ? new Date(s.endsWith('Z') ? s : s + 'Z').toLocaleDateString('es-CR') : '—';

export default function BaseConocimiento() {
  const [lista, setLista] = useState<Plantilla[]>([]);
  const [cargando, setCargando] = useState(true);
  const [subida, setSubida] = useState<Subida | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [aviso, setAviso] = useState<{ tipo: 'ok' | 'error'; texto: string } | null>(null);
  const [verInstrucciones, setVerInstrucciones] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const destino = useRef<{ id: number | null; numero: number | null }>({ id: null, numero: null });
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const cargar = () => {
    setCargando(true);
    axios.get(`${API}/conocimiento/`, { headers })
      .then(r => { setLista(r.data); setCargando(false); })
      .catch(() => setCargando(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, []);

  const mostrarAviso = (tipo: 'ok' | 'error', texto: string) => {
    setAviso({ tipo, texto });
    setTimeout(() => setAviso(null), 7000);
  };

  const descargar = async (p: Plantilla) => {
    try {
      const r = await axios.get(`${API}/conocimiento/${p.id}/descargar`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${String(p.numero).padStart(2, '0')}-${p.nombre}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      mostrarAviso('error', '❌ No se pudo descargar la plantilla');
    }
  };

  const elegirArchivo = (id: number | null, numero: number | null) => {
    destino.current = { id, numero };
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  const archivoElegido = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setTrabajando(true);
    try {
      const contenido = await f.text();
      const { id, numero } = destino.current;
      const r = await axios.post(`${API}/conocimiento/revisar`,
        { nombre: f.name, contenido, archivo_id: id }, { headers });
      setSubida({ archivoId: id, numero, nombre: f.name, contenido, revision: r.data });
    } catch {
      mostrarAviso('error', '❌ No se pudo revisar el archivo');
    } finally {
      setTrabajando(false);
    }
  };

  const publicar = async () => {
    if (!subida) return;
    setTrabajando(true);
    try {
      const r = await axios.post(`${API}/conocimiento/publicar`,
        { nombre: subida.nombre, contenido: subida.contenido, archivo_id: subida.archivoId }, { headers });
      mostrarAviso('ok', `✅ Plantilla ${r.data.numero} publicada: TicoBaterín ya usa esta versión`);
      setSubida(null);
      cargar();
    } catch (e: any) {
      mostrarAviso('error', e?.response?.data?.detail || '❌ No se pudo publicar');
    } finally {
      setTrabajando(false);
    }
  };

  const restaurar = async (p: Plantilla) => {
    if (!window.confirm(`¿Volver a la versión anterior de la Plantilla ${p.numero} (del ${fecha(p.anterior_en)})?`)) return;
    try {
      await axios.post(`${API}/conocimiento/${p.id}/restaurar`, {}, { headers });
      mostrarAviso('ok', `↩️ Plantilla ${p.numero} restaurada a la versión anterior`);
      cargar();
    } catch {
      mostrarAviso('error', '❌ No se pudo restaurar');
    }
  };

  const cambiarActivo = async (p: Plantilla) => {
    try {
      await axios.put(`${API}/conocimiento/${p.id}/activo`, {}, { headers });
      cargar();
    } catch {
      mostrarAviso('error', '❌ No se pudo cambiar el estado');
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <div className="flex justify-between items-start gap-4 flex-wrap mb-3">
        <div>
          <h2 className="font-bold text-azul">📚 Base de conocimiento (catálogos)</h2>
          <p className="text-gray-500 text-sm">
            Plantillas que TicoBaterín consulta para responder sobre motos, baterías SLA, Trojan, bicicletas, paneles solares, aceites y accesorios.
          </p>
        </div>
        {aviso && (
          <div className={`px-4 py-2 rounded-xl text-sm font-bold ${aviso.tipo === 'ok' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {aviso.texto}
          </div>
        )}
      </div>

      {/* ---------- Instrucciones ---------- */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-gray-700 mb-5">
        <button onClick={() => setVerInstrucciones(!verInstrucciones)} className="w-full flex justify-between items-center font-bold text-azul">
          <span>📝 Cómo actualizar un catálogo</span>
          <span className="text-gray-400 text-xs">{verInstrucciones ? 'Ocultar ▲' : 'Ver ▼'}</span>
        </button>
        {verInstrucciones && (
          <ol className="list-decimal ml-5 mt-3 space-y-1.5">
            <li>Presione <b>Descargar</b> en la plantilla que quiere cambiar.</li>
            <li>Ábrala con el <b>Bloc de notas</b> (no con Word). Al inicio del archivo vienen las instrucciones completas.</li>
            <li>Cambie precios o productos. <b>No borre los “##”</b>: marcan dónde empieza cada sección.</li>
            <li>Guarde el archivo y presione <b>Subir versión nueva</b> en la <b>misma plantilla</b>.</li>
            <li>Revise la vista previa. Si no hay errores, presione <b>Publicar</b>: el cambio aplica al instante.</li>
            <li>Si algo quedó mal, use <b>Restaurar anterior</b> para volver a la versión previa.</li>
          </ol>
        )}
        <p className="mt-3 text-xs text-gray-500">
          Los catálogos indican precios válidos por 10 días: las plantillas con más de 10 días sin actualizar se marcan en naranja.
        </p>
      </div>

      <input ref={inputRef} type="file" accept=".md,.txt,text/markdown,text/plain" className="hidden" onChange={archivoElegido} />

      {/* ---------- Revisión antes de publicar ---------- */}
      {subida && (
        <div className="border-2 border-azul rounded-2xl p-5 mb-5">
          <h3 className="font-bold text-azul mb-1">
            🔍 Revisión: {subida.numero ? `Plantilla ${subida.numero}` : 'Plantilla nueva'}
          </h3>
          <p className="text-sm text-gray-500 mb-3">
            Archivo: <b>{subida.nombre}</b>{subida.revision.titulo ? ` · ${subida.revision.titulo}` : ''}
          </p>

          {subida.revision.errores.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 text-sm text-red-700 space-y-1">
              <p className="font-bold">❌ Hay que corregir esto antes de publicar:</p>
              {subida.revision.errores.map((e, i) => <p key={i}>• {e}</p>)}
            </div>
          )}
          {subida.revision.advertencias.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-3 text-sm text-orange-700 space-y-1">
              <p className="font-bold">⚠️ Revise (se puede publicar igual):</p>
              {subida.revision.advertencias.map((e, i) => <p key={i}>• {e}</p>)}
            </div>
          )}

          <p className="text-sm font-bold text-gray-700 mb-2">
            {subida.revision.fragmentos.length} secciones encontradas:
          </p>
          <div className="max-h-56 overflow-y-auto border border-gray-100 rounded-xl divide-y divide-gray-100 mb-4">
            {subida.revision.fragmentos.map(f => (
              <div key={f.orden} className="px-3 py-2 text-sm flex justify-between gap-3">
                <span className="text-gray-800">{f.orden}. {f.titulo}</span>
                <span className={`shrink-0 text-xs ${f.caracteres > 900 ? 'text-orange-600 font-bold' : 'text-gray-400'}`}>
                  {f.caracteres} car.{!f.tiene_etiquetas && ' · sin etiquetas'}
                </span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={publicar}
              disabled={trabajando || !subida.revision.se_puede_publicar}
              className="bg-azul text-white py-2.5 rounded-xl font-bold hover:bg-azul-dark disabled:opacity-40"
            >
              {trabajando ? '⏳ Publicando...' : '✅ Publicar'}
            </button>
            <button onClick={() => setSubida(null)} className="border border-gray-200 py-2.5 rounded-xl font-bold text-gray-600 hover:bg-gray-50">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* ---------- Lista de plantillas ---------- */}
      {cargando ? (
        <p className="text-center text-gray-400 text-sm py-6">Cargando plantillas...</p>
      ) : (
        <div className="space-y-3">
          {lista.map(p => (
            <div key={p.id} className={`border rounded-xl p-4 ${p.activo ? 'border-gray-100' : 'border-gray-100 bg-gray-50 opacity-70'}`}>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 shrink-0 rounded-xl bg-azul text-white flex flex-col items-center justify-center leading-none">
                  <span className="text-[9px] opacity-80">PLANT.</span>
                  <span className="font-bold text-lg">{p.numero}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-800 break-words">{p.titulo}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {String(p.numero).padStart(2, '0')}-{p.nombre} · {p.fragmentos} secciones · actualizada el {fecha(p.actualizado_en)}
                  </p>
                  <div className="flex gap-2 mt-1.5 flex-wrap">
                    {p.vencido && (
                      <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold">
                        ⏰ {p.dias} días sin actualizar: revise los precios
                      </span>
                    )}
                    {!p.activo && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-bold">
                        Desactivada: TicoBaterín no la usa
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-2 flex-wrap mt-3">
                <button onClick={() => descargar(p)} className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50">
                  ⬇️ Descargar
                </button>
                <button
                  onClick={() => elegirArchivo(p.id, p.numero)}
                  disabled={trabajando}
                  className="text-xs bg-azul text-white rounded-lg px-3 py-1.5 hover:bg-azul-dark disabled:opacity-50"
                >
                  ⬆️ Subir versión nueva
                </button>
                {p.tiene_anterior && (
                  <button onClick={() => restaurar(p)} className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50">
                    ↩️ Restaurar anterior ({fecha(p.anterior_en)})
                  </button>
                )}
                <button onClick={() => cambiarActivo(p)} className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50">
                  {p.activo ? '⏸️ Desactivar' : '▶️ Activar'}
                </button>
              </div>
            </div>
          ))}

          {lista.length === 0 && (
            <p className="text-center text-gray-400 text-sm py-6">Todavía no hay plantillas cargadas.</p>
          )}

          <button
            onClick={() => elegirArchivo(null, null)}
            disabled={trabajando}
            className="w-full border-2 border-dashed border-gray-200 rounded-xl py-3 text-sm font-bold text-gray-500 hover:border-azul hover:text-azul disabled:opacity-50"
          >
            ➕ Agregar plantilla nueva (otro catálogo)
          </button>
        </div>
      )}
    </div>
  );
}
